import React from "react"
import { Form, useParams, useSearchParams } from "react-router-dom"
import {
  Flex,
  HStack,
  Box,
  Select,
  Input,
  Button,
  Text,
  Link,
} from "@chakra-ui/react"
import { AddIcon, MinusIcon } from "@chakra-ui/icons"
import { FaFilter } from "react-icons/fa"
import { FaX } from "react-icons/fa6"
import {
  Schemas,
  Limit,
  Offset,
  CurrentFilters,
  Pagination,
} from "../lib/store"
import {
  MAX_STRING_FILTER_COMBINATIONS,
  CONTACT_SALES_URL,
} from "../lib/limits"
import { addAlert, AlertType } from "../lib/alerts"

const TableFilter = () => {
  const [schemas] = Schemas.useState()
  const { schemaId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [limit] = Limit.useState()
  const [offset, setOffset] = Offset.useState()
  const [pagination] = Pagination.useState()

  // Reset the filters and offset when the schema changes
  React.useEffect(() => {
    clearFilters()
    setOffset(0)
  }, [schemaId])

  // Update URL when limit/offset/pagination change
  React.useEffect(() => {
    if (!isNaN(limit) && !isNaN(offset)) {
      const currentParams = Object.fromEntries(searchParams.entries())
      const newParams = {
        ...currentParams,
        limit: limit.toString(),
        offset: offset.toString(),
      }
      setSearchParams(newParams)
    }
  }, [limit, offset, pagination, setSearchParams, searchParams])

  // Find the selected schema
  const schema = React.useMemo(() => schemas[schemaId], [schemas, schemaId])

  const properties = React.useMemo(
    () => schema?.schema?.properties || {},
    [schema],
  )
  // Clear the filters and reset the search params
  const [filters, setFilters] = React.useState([
    { label: "", operator: "", value: "", value2: "" },
  ])

  const clearFilters = React.useCallback(() => {
    setFilters([{ label: "", operator: "=", value: "", value2: "" }])
    CurrentFilters.setValue([])
    setOffset(0)
    setSearchParams({})
  }, [setFilters, setOffset, setSearchParams])

  // Only update the filters if they actually changed
  const handleSearch = React.useCallback(
    (e) => {
      e?.preventDefault()

      // Batch the filter updates
      const newFilters = filters.reduce((acc, filter) => {
        if (!filter.label || !filter.value) return acc

        let newValue = filter.value
        if (filter.value2) {
          newValue = `${filter.value}${filter.operator}${filter.value2}`
        } else if (filter.operator !== "=") {
          newValue = `${filter.operator}${filter.value}`
        }

        return [...acc, { label: filter.label, value: newValue }]
      }, [])

      const stringFilterCount = newFilters.filter(
        (filter) => properties[filter.label]?.type === "string",
      ).length

      if (stringFilterCount > MAX_STRING_FILTER_COMBINATIONS) {
        addAlert(
          AlertType.ERROR,
          "Too Many Text Filters",
          React.createElement(
            React.Fragment,
            null,
            `This query uses ${stringFilterCount} text filters. The maximum allowed is ${MAX_STRING_FILTER_COMBINATIONS} text filters. Please reduce the number of text filters or `,
            React.createElement(
              Link,
              { href: CONTACT_SALES_URL, isExternal: true, color: "blue.500" },
              "contact sales",
            ),
            " for higher limits.",
          ),
          10,
        )
        return
      }

      // Only update if filters actually changed
      if (
        JSON.stringify(CurrentFilters.getValue()) !== JSON.stringify(newFilters)
      ) {
        setOffset(0)
        CurrentFilters.setValue(newFilters)
      }
    },
    [filters, properties, setOffset],
  )

  const handleAddFilter = React.useCallback(() => {
    setFilters([...filters, { label: "", operator: "", value: "", value2: "" }])
  }, [filters, setFilters])

  const handleRemoveFilter = React.useCallback(() => {
    setFilters([...filters.slice(0, -1)])
  }, [filters, setFilters])

  const handleInputChange = React.useCallback(
    (e, index) => {
      const values = [...filters]
      if (e.target.name === "label") {
        values[index].label = e.target.value
      } else if (e.target.name === "operator") {
        values[index].operator = e.target.value
      } else if (e.target.name === "value") {
        values[index].value = e.target.value
      } else {
        values[index].value2 = e.target.value
      }
      setFilters(values)
    },
    [filters, setFilters],
  )

  const filterMenuItems = React.useMemo(() => {
    const entries = Object.entries(properties)
    if (!entries.length) return []
    return entries
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name]) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))
  }, [properties])

  return (
    <Form id="filter-set">
      <Flex
        mt="1em"
        direction="row"
        gap="2"
        verticalAlign="middle"
        alignItems="center"
        wrap="wrap"
      >
        <Flex flexWrap="wrap" gap="2">
          {filters.map((filter, index) => (
            <HStack
              key={index}
              bg="white"
              verticalAlign="middle"
              alignItems="center"
              maxheight="2em"
            >
              <Select
                name="label"
                label="Column"
                onChange={(e) => handleInputChange(e, index)}
                value={filter.label ?? ""}
                title="Select a column to filter by"
                size="sm"
              >
                <option>Column</option>
                {filterMenuItems}
              </Select>
              {filter.label &&
                ["number", "integer"].includes(
                  properties[filter.label]?.type,
                ) && (
                  <HStack verticalAlign="middle" padding="0.25em">
                    <Box minW="3em">
                      <Select
                        bg="Gray.100"
                        borderRadius="0.25em"
                        verticalAlign="middle"
                        variant="filterOperand"
                        name="operator"
                        onChange={(e) => handleInputChange(e, index)}
                        defaultValue="="
                        width="4em"
                      >
                        <option value="=" title="equals">
                          =
                        </option>
                        <option value=">" title="greater than">
                          &gt;
                        </option>
                        <option value="<" title="less than">
                          &lt;
                        </option>
                        <option value=">=" title="greater than or equal to">
                          &ge;
                        </option>
                        <option value="<=" title="less than or equal to">
                          &le;
                        </option>
                        <option value="~" title="between">
                          &lt; .. &gt;
                        </option>
                      </Select>
                    </Box>
                    <Input
                      maxW="4em"
                      minW="4em"
                      variant="filter"
                      type="number"
                      name="value"
                      step="any"
                      value={filter.searchValue}
                      onChange={(e) => handleInputChange(e, index)}
                      placeholder="Value"
                      title="Enter a value to filter by"
                      size="small"
                    />
                  </HStack>
                )}
              {filter.operator && filter.operator === "~" && (
                <Input
                  ml="0.25em"
                  maxW="4em"
                  minW="4em"
                  variant="filter"
                  type="number"
                  name="value2"
                  step="any"
                  value={filter.searchValue2}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Value"
                  title="Enter a max value to filter by"
                  size="small"
                  mr="0.25em"
                />
              )}
              {filter.label && properties[filter.label]?.type === "string" && (
                <>
                  <Text>=</Text>
                  <Input
                    width="8em"
                    variant="filter"
                    value={filter.searchValue}
                    onChange={(e) => handleInputChange(e, index)}
                    name="value"
                    placeholder="Value"
                    size="small"
                    title="Value to filter by"
                  />
                </>
              )}
            </HStack>
          ))}
        </Flex>
        <HStack>
          {filters.length > 1 && (
            <Button
              variant="grayRounded"
              size="xs"
              onClick={handleRemoveFilter}
            >
              <MinusIcon />
            </Button>
          )}
          {filters.length < filterMenuItems.length && filters.length > 0 && (
            <Button variant="grayRounded" size="xs" onClick={handleAddFilter}>
              <AddIcon />
            </Button>
          )}
          <Button
            variant="primary"
            type="submit"
            onClick={handleSearch}
            rightIcon={<FaFilter />}
            title="Filter data"
            size="sm"
          >
            Filter
          </Button>
          <Button
            as="Link"
            onClick={clearFilters}
            buttonStyle="inline"
            rightIcon={<FaX />}
            title="Clear filters"
            size="sm"
          >
            Clear filters
          </Button>
        </HStack>
      </Flex>
    </Form>
  )
}

export default TableFilter
